import { IconButton, Menu, MenuItem, Typography } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import UploadIcon from 'assets/icons/UploadIcon';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import languageValues from 'utils/LanguageUtils';
import SnackbarUtils from 'utils/SnackbarUtils';
import './index.scss';
import { DocumentCoopProps, DocumentItem } from './__types';
import { downloadDocumentoCooperativa } from 'store/modules/EscolasActions';
import UseProgramas from '../../../../../../../hooks/useCooperativa';
import useCadastroSteps from 'pages/Escola/Cadastro/etapas/ProgramasEducacionais/hooks/useEscolaSteps';

export const DocumentCoop: React.FC<DocumentCoopProps> = ({
  documents,
  onDocumentsChange,
  onFilesRemove,
  onFilesChange,
}) => {
  const dispatch = useDispatch();
  const tipoDocumentoCoop = useSelector(
    (state: RootState) => state.escolas.tipoDocumentoCoop
  );
  const savingCoops = useSelector((state: RootState) => state.escolas.savingCoops);
  const [documentItems, setDocumentItems] = useState<DocumentItem[]>([]);
  const [removedItems, setRemovedItems] = useState<number[]>([]);

  const { handleTipoDocumentoFetch } = useCadastroSteps();
  const { handleDownloadLink } = UseProgramas();

  useEffect(() => {
    if (tipoDocumentoCoop.length === 0) {
      handleTipoDocumentoFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (tipoDocumentoCoop && documentItems.length === 0) ||
      documents?.length === 0
    ) {
      tipoDocumentoCoop.forEach((item) => {
        const existingDoc = documents?.find((doc) => doc.tipo === item.tipo);
        setDocumentItems((state) => {
          return [
            ...state,
            {
              label: item.nome,
              type: item.tipo,
              required: item.obrigatorio,
              uploaded: false,
              file: null,
              path: existingDoc?.caminho ?? null,
              anchorEl: null,
            },
          ];
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoDocumentoCoop, documents]);

  useEffect(() => {
    const requiredDocumentsUploaded = documentItems
      .filter((item) => item.required)
      .every((item) => item.uploaded || item.path !== null);
    onDocumentsChange(requiredDocumentsUploaded);

    onFilesChange(documentItems);
    onFilesRemove?.(removedItems);
  }, [
    documentItems,
    removedItems,
    onDocumentsChange,
    onFilesChange,
    onFilesRemove,
  ]);

  const handleOpenMenu = (
    index: number,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    const newDocumentItems = [...documentItems];
    newDocumentItems[index].anchorEl = event.currentTarget;
    setDocumentItems(newDocumentItems);
  };

  const handleCloseMenu = (index: number) => {
    const newDocumentItems = [...documentItems];
    newDocumentItems[index].anchorEl = null;
    setDocumentItems(newDocumentItems);
  };

  const handleFileChange = async (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newDocumentItems = [...documentItems];
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.type !== 'application/pdf') {
        SnackbarUtils.enqueueSnackbar({
          message: 'Somente arquivos .pdf',
          options: {
            variant: 'error',
          },
        });
        return;
      }
      newDocumentItems[index].file = file;
      newDocumentItems[index].uploaded = true;
    }
    setDocumentItems(newDocumentItems);
  };

  const handleDownload = async (index: number) => {
    const { file, path } = documentItems[index];

    if (path) {
      dispatch(downloadDocumentoCooperativa(path, (resp) => {
        handleDownloadLink(new File([resp.data], path, { type: 'application/pdf' }));
      }));
      return;
    }

    if (file) {
      handleDownloadLink(file);
    }
  };

  const handleDelete = async (index: number) => {
    const newDocumentItems = [...documentItems];

    newDocumentItems[index].file = null;
    newDocumentItems[index].path = null;
    newDocumentItems[index].uploaded = false;
    newDocumentItems[index].anchorEl = null;
    setRemovedItems((ri) => [
      ...ri,
      newDocumentItems[index].type,
    ]);
    setDocumentItems(newDocumentItems);
  };

  const isDocumentUploaded = useCallback((documentItem: DocumentItem) => {
    return documentItem.file || documentItem.uploaded || documentItem.path;
  }, []);

  const requiredLabels = [
    languageValues.pages.escolas.labels.AtaDaCooperativaEscolar,
    languageValues.pages.escolas.labels.EstatutoDaCooperativaEscolar
  ];

  return (
    <div className='document-coop'>
      <div>
        <Typography className='document-coop__title'>
          {languageValues.pages.escolas.labels.documentos}
        </Typography>
      </div>
      {documentItems.map((documentItem, index) => (
        <div key={index} className='document-coop__item-container'>
          <span
            className={`document-coop__label ${isDocumentUploaded(documentItem) ? '' : 'gray'
              }`}
          >
            {documentItem.label}
            {requiredLabels.includes(documentItem.label) &&
              <span
                className='document-coop_required_label'
                style={{
                  fontSize: '0.6rem',
                  margin: '0 0 0 2px',
                  fontWeight: 'bold'
                }}>
                {languageValues.pages.escolas.labels.Obrigatorio}
              </span>}
          </span>
          {isDocumentUploaded(documentItem) ? (
            <IconButton
              className='document-coop__icon-button'
              color='primary'
              disabled={savingCoops}
              onClick={(event) => handleOpenMenu(index, event)}
            >
              <MoreVertIcon />
            </IconButton>
          ) : (
            <IconButton
              className='document-coop__icon-button'
              color='primary'
              disabled={savingCoops}
              component='label'
            >
              <UploadIcon />
              <input
                className={documentItem.type.toString()}
                type='file'
                onChange={(event) => handleFileChange(index, event)}
                style={{ display: 'none' }}
              />
            </IconButton>
          )}
          <Menu
            anchorEl={documentItem.anchorEl}
            open={Boolean(documentItem.anchorEl)}
            onClose={() => handleCloseMenu(index)}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'top',
            }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
          >
            <MenuItem onClick={() => handleDownload(index)}>
              {languageValues.pages.escolas.buttons.download}
            </MenuItem>
            <MenuItem onClick={() => handleDelete(index)}>
              {languageValues.pages.escolas.buttons.excluir}
            </MenuItem>
          </Menu>
        </div>
      ))}
    </div>
  );
};
